"use client";

import * as React from "react";
import { ArrowRight, BookOpen, Clock, ShieldCheck, UserCheck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader } from "@/components/ui/card";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
interface FallbackArticle {
  id: string;
  title: string;
  excerpt: string;
  link: string;
  readTime: string;
  authorName: string;
  authorRole: string;
  category: string;
  imageSrc: string;
  imageSrcset: string;
  imageSizes: string;
  imageAlt: string;
}
const fallbackArticles: FallbackArticle[] = [{
  id: "behavior-1",
  title: "How to help an anxious cat feel secure at home",
  excerpt: "Recognize subtle stress markers in felines and set up environmental modifications that lower cortisol levels and restore calm.",
  link: "/BehaviorTrainingPage",
  readTime: "6 min read",
  authorName: "Dr. Elena Vance, DVM",
  authorRole: "Veterinary Behaviorist",
  category: "Feline Behavior",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_calm_environment.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_calm_environment.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/cat_calm_environment.png-wvc-sizes",
  imageAlt: "Calm domestic cat resting comfortably in a quiet sunlit indoor space"
}, {
  id: "behavior-2",
  title: "Mastering loose-leash walking with high-energy dogs",
  excerpt: "Step-by-step reinforcement drills to replace leash pulling with voluntary check-ins and relaxed stride pacing on daily walks.",
  link: "/BehaviorTrainingPage",
  readTime: "8 min read",
  authorName: "Marcus Sterling, CPDT-KA",
  authorRole: "Certified Behavior Consultant",
  category: "Canine Training",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_leash_training.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_leash_training.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_leash_training.png-wvc-sizes",
  imageAlt: "Golden retriever walking politely on a loose leash beside owner outdoors"
}, {
  id: "behavior-3",
  title: "Stopping destructive chewing through redirection",
  excerpt: "Identify the root cause between teething, boredom, and separation anxiety, with targeted enrichment items that protect your home.",
  link: "/BehaviorTrainingPage",
  readTime: "5 min read",
  authorName: "Dr. Aris Thorne, DVM",
  authorRole: "Clinical Animal Behaviorist",
  category: "Problem Solving",
  imageSrc: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_chewing_enrichment.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_chewing_enrichment.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1780169/img/dog_chewing_enrichment.png-wvc-sizes",
  imageAlt: "Puppy engaging with an appropriate durable chew toy on a living room rug"
}];
function BehaviorArticlesConsumer() {
  const {
    posts,
    loading,
    error,
    refetch
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {[0, 1, 2].map(i => <div key={i} data-index={i} className="flex flex-col overflow-hidden rounded-xl border border-border bg-card p-0 shadow-sm">
            <div className="aspect-[16/10] w-full animate-pulse bg-muted" />
            <div className="flex flex-1 flex-col p-6 space-y-4">
              <div className="h-4 w-24 animate-pulse rounded bg-muted" />
              <div className="h-6 w-full animate-pulse rounded bg-muted" />
              <div className="h-4 w-3/4 animate-pulse rounded bg-muted" />
              <div className="mt-auto pt-4 border-t border-border flex items-center justify-between">
                <div className="h-4 w-28 animate-pulse rounded bg-muted" />
                <div className="h-4 w-16 animate-pulse rounded bg-muted" />
              </div>
            </div>
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="rounded-xl border border-border bg-card p-8 text-center">
        <p className="text-muted-foreground text-sm">
          Unable to load recent behavior guides right now.
        </p>
        <Button variant="outline" size="sm" onClick={() => refetch()} className="mt-4">
          Try again
        </Button>
      </div>;
  }
  const displayList = posts && posts.length > 0 ? posts.slice(0, 3) : null;
  if (displayList) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
        {displayList.map((post, i) => <article key={post.id} data-index={i} className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm transition-[border-color,box-shadow,transform] duration-250 hover:-translate-y-1 hover:border-border hover:shadow-md">
            <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
              {post.featuredImage ? <img src={post.featuredImage} alt={post.title} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" /> : <div className="flex h-full w-full items-center justify-center bg-muted text-muted-foreground">
                  <BookOpen className="size-8 stroke-[1.75]" />
                </div>}
              <div className="absolute top-3 left-3">
                <span className="inline-flex items-center rounded-full border border-border/80 bg-background/90 px-2.5 py-1 text-xs font-semibold text-foreground backdrop-blur-xs">
                  {post.categories?.[0]?.name || "Behavior"}
                </span>
              </div>
            </div>

            <CardHeader className="flex-1 p-6 pb-4">
              <h3 className="font-serif text-xl font-semibold leading-snug tracking-tight text-foreground line-clamp-2">
                <Link to={post.link || "/BehaviorTrainingPage"} className="transition-colors hover:text-foreground/80 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">
                  {post.title}
                </Link>
              </h3>
              {post.excerpt && <p className="mt-2 text-sm leading-relaxed text-muted-foreground line-clamp-2">
                  {post.excerpt.replace(/<[^>]*>?/gm, "")}
                </p>}
            </CardHeader>

            <CardContent className="px-6 py-0">
              <div className="rounded-md border-l-2 border-l-foreground border-border/60 bg-muted/40 p-2.5 text-xs text-muted-foreground">
                <span className="font-semibold text-foreground">Clinical note:</span> Focuses on fear-free reinforcement protocols.
              </div>
            </CardContent>

            <CardFooter className="mt-auto flex items-center justify-between border-t border-border p-6 pt-4 text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <UserCheck className="size-4 shrink-0 text-foreground" />
                <span className="font-medium text-foreground">
                  {post.author?.name || "Dr. Staff Veterinarian"}
                </span>
              </div>
              <Link to={post.link || "/BehaviorTrainingPage"} className="inline-flex items-center gap-1 font-semibold text-foreground hover:underline">
                Read
                <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-0.5" />
              </Link>
            </CardFooter>
          </article>)}
      </div>;
  }
  return <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
      {fallbackArticles.map((article, i) => <article key={article.id} data-index={i} className="group flex flex-col overflow-hidden rounded-xl border border-border bg-card text-card-foreground shadow-sm transition-[border-color,box-shadow,transform] duration-250 hover:-translate-y-1 hover:border-border hover:shadow-md">
          <div className="relative aspect-[16/10] w-full overflow-hidden bg-muted">
            <img src={article.imageSrc} data-wvc-srcset={article.imageSrcset} data-wvc-sizes={article.imageSizes} alt={article.imageAlt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-500 ease-out group-hover:scale-105" />
            <div className="absolute top-3 left-3">
              <span className="inline-flex items-center rounded-full border border-border/80 bg-background/90 px-2.5 py-1 text-xs font-semibold text-foreground backdrop-blur-xs">
                {article.category}
              </span>
            </div>
          </div>

          <CardHeader className="flex-1 p-6 pb-4">
            <h3 className="font-serif text-xl font-semibold leading-snug tracking-tight text-foreground line-clamp-2">
              <Link to={article.link} className="transition-colors hover:text-foreground/80 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring">
                {article.title}
              </Link>
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground line-clamp-2">
              {article.excerpt}
            </p>
          </CardHeader>

          <CardContent className="px-6 py-0">
            <div className="rounded-md border-l-2 border-l-foreground border-border/60 bg-muted/40 p-2.5 text-xs text-muted-foreground">
              <span className="font-semibold text-foreground">Clinical takeaway:</span> Humane conditioning without intimidation or force.
            </div>
          </CardContent>

          <CardFooter className="mt-auto flex items-center justify-between border-t border-border p-6 pt-4 text-xs text-muted-foreground">
            <div className="flex flex-col">
              <div className="flex items-center gap-1.5">
                <UserCheck className="size-4 shrink-0 text-foreground" />
                <span className="font-medium text-foreground">{article.authorName}</span>
              </div>
              <span className="text-[11px] text-muted-foreground pl-5.5">{article.authorRole}</span>
            </div>
            <div className="flex items-center gap-1 text-muted-foreground">
              <Clock className="size-3.5" />
              <span>{article.readTime}</span>
            </div>
          </CardFooter>
        </article>)}
    </div>;
}
export default function BehaviorArticles() {
  const wpQuery = React.useMemo(() => {
    return new WP_Query({
      post_type: "post",
      per_page: 3,
      order: "desc",
      orderby: "date"
    });
  }, []);
  return <section id="featured-behavior-articles" data-nav="light" className="bg-background py-16 md:py-24 text-foreground border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 flex flex-col items-start justify-between gap-6 border-b border-border pb-8 md:flex-row md:items-end">
          <div className="max-w-2xl">
            <div className="mb-3 inline-flex items-center gap-2 rounded-md bg-muted px-2.5 py-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              <ShieldCheck className="size-3.5 text-foreground" />
              <span>Veterinary Behavior Standards</span>
            </div>
            <h2 className="font-serif text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
              Evidence-informed behavior guides
            </h2>
            <p className="mt-3 text-base text-muted-foreground sm:text-lg">
              Understand why pets act out and build reliable habits using positive reinforcement techniques reviewed by veterinary behaviorists.
            </p>
          </div>

          <div className="shrink-0">
            <Button asChild variant="outline" className="gap-2 font-medium">
              <Link to="/BehaviorTrainingPage">
                <span>View all training guides</span>
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>

        <PostsProvider wp_query={wpQuery} className="w-full">
          <BehaviorArticlesConsumer />
        </PostsProvider>
      </div>
    </section>;
}